import React, { useState, useMemo } from 'react';
import { InformationalCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import { Body, SectionHeader } from '@grnhse/seedling/lib/birch/typography';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid';
import { withLocalePrefix } from '../shared/utils/translation';
import ConsentAction from './consent_action';
import type { ScopeInfo, ScopeGroup } from './scope_utils';
import { groupScopes } from './scope_utils';
import { LogoContainer } from './styled_components';
import { ScopeGroupSection } from './scope_components';

const t = withLocalePrefix('authorization.authorization_form');

type Props = {
  name: string;
  scopes: ScopeInfo[];
  accountSettingsUrl: string;
  companyLogoUrl: string;
  denyUrl: string;
};

export default function PublicClientConsentForm({
  name,
  scopes,
  accountSettingsUrl,
  companyLogoUrl,
  denyUrl,
}: Props) {
  const groups = useMemo(() => groupScopes(scopes, t), [scopes]);

  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(
    () => new Set()
  );

  const onToggleScope = (scopeName: string) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scopeName)) {
        next.delete(scopeName);
      } else {
        next.add(scopeName);
      }
      return next;
    });
  };

  const onToggleSubGroup = (scopeInfos: ScopeInfo[], checked: boolean) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      for (const s of scopeInfos) {
        if (checked) {
          next.add(s.name);
        } else {
          next.delete(s.name);
        }
      }
      return next;
    });
  };

  const onToggleProduct = (group: ScopeGroup, checked: boolean) => {
    const allScopes = [...group.readScopes, ...group.writeScopes];
    onToggleSubGroup(allScopes, checked);
  };

  const onCancel = () => {
    window.location.href = denyUrl;
  };

  return (
    <Stack>
      <SectionHeader>
        {t('title.authorize')} <strong>{name}</strong>{' '}
        {t('title.to_use_GH_account')}
      </SectionHeader>
      <InformationalCallOut bodyText={t('beta_callout')} />
      <div>
        <Grid container>
          <Grid item xs={7}>
            {scopes.length === 0 ? (
              <InformationalCallOut bodyText={t('no_consentable_scopes')} />
            ) : (
              <>
                <Body>{t('scope_description')}</Body>
                <input
                  type="hidden"
                  name="scope"
                  value={Array.from(selectedScopes).join(' ')}
                />
                <Stack spacing={2}>
                  {groups.map((group) => (
                    <ScopeGroupSection
                      key={group.product}
                      group={group}
                      selectedScopes={selectedScopes}
                      onToggleScope={onToggleScope}
                      onToggleSubGroup={onToggleSubGroup}
                      onToggleProduct={onToggleProduct}
                      translationFn={t}
                    />
                  ))}
                </Stack>
              </>
            )}
            <ConsentAction
              accountSettingsUrl={accountSettingsUrl}
              onCancel={onCancel}
              disabled={selectedScopes.size === 0}
            />
          </Grid>
          {companyLogoUrl && (
            <Grid item xs={5}>
              <LogoContainer>
                <img src={companyLogoUrl} alt={name} />
              </LogoContainer>
            </Grid>
          )}
        </Grid>
      </div>
    </Stack>
  );
}
